import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import './UserNav.css';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: '☀️' },
  { value: 'dark', label: 'Dark', icon: '🌙' },
  { value: 'system', label: 'Standard', icon: '💻' },
];

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const current = THEME_OPTIONS.find(t => t.value === theme) || THEME_OPTIONS[2];

  const cycle = () => {
    const idx = THEME_OPTIONS.findIndex(t => t.value === theme);
    const next = THEME_OPTIONS[(idx + 1) % THEME_OPTIONS.length];
    setTheme(next.value);
  };

  return (
    <button
      className="theme-toggle-btn"
      onClick={cycle}
      title={`Theme: ${current.label} — click to switch`}
      aria-label={`Current theme: ${current.label}. Click to switch theme.`}
      type="button"
    >
      <span className="theme-toggle-icon">{current.icon}</span>
      <span className="theme-toggle-label">{current.label}</span>
    </button>
  );
}

function UserNav({ onHelpToggle }) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Helper function to determine if a link is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Log user tier for mobile debugging
  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  if (!isAuthenticated) {
    return (
      <nav className={`user-nav ${isMobileMenuOpen ? 'menu-open' : ''}`}>
        <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        {/* Theme toggle - always visible */}
        <ThemeToggle />
        {/* Mobile-only Sign In button - always visible */}
        <Link to="/auth" className="nav-button mobile-login-button">Sign In</Link>
        <div className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link 
            to="/" 
            className={`nav-link home-link ${isActive('/') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            New Transcript
          </Link>
          <Link
            to="/instructions"
            className={`nav-link ${isActive('/instructions') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Instructions
          </Link>
          <Link
            to="/about"
            className={`nav-link ${isActive('/about') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            About
          </Link>
          <Link
            to="/plans-and-payment"
            className={`nav-link ${isActive('/plans-and-payment') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Plans
          </Link>
          <Link 
            to="/contact" 
            className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Contact Us
          </Link>
          <Link 
            to="/auth" 
            className="nav-button desktop-login-button" 
            onClick={closeMobileMenu}
          >
            Sign In
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav className={`user-nav ${isMobileMenuOpen ? 'menu-open' : ''}`}>
      <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
        <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>
      {/* Theme toggle - always visible */}
      <ThemeToggle />
      <div className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <Link 
          to="/" 
          className={`nav-link home-link ${isActive('/') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          New Transcript
        </Link>
        <Link
          to="/instructions"
          className={`nav-link ${isActive('/instructions') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          Instructions
        </Link>
        <Link
          to="/about"
          className={`nav-link ${isActive('/about') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          About
        </Link>
        <Link
          to="/dashboard"
          className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          Dashboard
        </Link>
        <Link 
          to="/settings" 
          className={`nav-link ${isActive('/settings') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          Settings
        </Link>
        <Link 
          to="/contact" 
          className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          Contact Us
        </Link>
        <div className="user-menu">
          <Link to="/settings" className="user-email" onClick={closeMobileMenu}>{user.email}</Link>
          <button onClick={handleLogout} className="nav-button logout-button">
            Sign Out
          </button>
        </div>
      </div>
    </nav>
  );
}

export default UserNav;
